import React from 'react';
import ReactDOM from 'react-dom';
import { AppWithMSALAuth } from './structure/AppWithMSALAuth';
import { mergeStyles } from '@fluentui/react';
import reportWebVitals from './reportWebVitals';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { apiConfig, MSALConfigGen } from './authConfig';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { RootState } from './state/RootState';
import { MSALAuth } from './state/authentication/MSALAuth';
import { TeamsSSOAuth } from './state/authentication/TeamsSSOAuth';
import { TeamsMsal2Provider } from '@microsoft/mgt-teams-msal2-provider';
import * as microsoftTeams from '@microsoft/teams-js';
import SubscriptionState from './state/SubscriptionState';
import { StandaloneSaasActivation } from './components/subscription/StandaloneSaasActivation';
import { SSOAuth } from './state/authentication/SSOAuth';
import { OutlookSSOAuth } from './state/authentication/OutlookSSOAuth';
import PageRouter from './components/pages/PageRouter';
import { OutlookCtrlState } from './outlook/OutlookCtrl';
import { AuthenticatedApp } from './structure/AuthenticatedApp';

let hashParts = (window.location.hash || '').replace('#', '').split('/');
if(hashParts.length === 2 && hashParts[0] === 'redirect') {
	window.location.href = decodeURIComponent(hashParts[1]);
	throw new Error('redirecting');
}

initializeIcons();

// Inject some global styles
mergeStyles({
	':global(body,html,#root)': {
		margin: 0,
		padding: 0,
		height: '100vh',
	},
});

function requestScopeConsent(scopes: string[]) {
	var requestObj = {
		scopes: scopes,
		prompt: 'select_account',
	};

	const msalConfig = MSALConfigGen.getMsalConfig();

	const clientApp = new PublicClientApplication({
		auth: {
			clientId: msalConfig.auth.clientId,
			redirectUri: msalConfig.auth.redirectUri,
		},
	});

	clientApp.handleRedirectPromise().then(() => {
		//const accounts = clientApp.getAllAccounts();
		clientApp.loginRedirect(requestObj);
	});
}

if (window.location.href.indexOf('admin_consent=True') > 0) {
	//This is an auth popup
	window.close();
}

//If we're just activating the saas token, it doesn't require a login or anything, so just show that component
let saasToken = RootState.getUrlParameter('token');
if (saasToken) {
	var subState = new SubscriptionState();
	subState.startConfirmingPaymentToken(saasToken);
	ReactDOM.render(
		<React.StrictMode>
			<StandaloneSaasActivation subscription={subState}></StandaloneSaasActivation>
		</React.StrictMode>,
		document.getElementById('root'),
	);
} else {
	let inPopup: boolean = false;
	const inIframe = window.self !== window.top;
	let isTeamsIFrame =
		window.name === 'embedded-page-container' ||
		window.name === 'extension-tab-frame' ||
		(inIframe && window.location.hash === '#teamsEntryPoint');

	if (isTeamsIFrame) {
		TeamsMsal2Provider.microsoftTeamsLib = microsoftTeams;
	}

	if(window.location.hash === '#requestcomplete') {

		let root = document.getElementById('root');
		if (root) {
			root.innerHTML = '<div>Request completed. Please close this page. <script>window.close();</script></div>';
		}

		inPopup = true;
	}

	//Create a temporary root state. It doesn't have the right auth. We don't know yet if we're in Teams or not
	var tempRootState = new RootState(new SSOAuth(), true);

	if (
		tempRootState.page === 'authComplete' ||
		(RootState.isOAuthRedirect() &&
			!window.menubar
				.visible) /* Hacky McHackface to detect if it's not in a popup window */
	) {
		inPopup = true;

		let root = document.getElementById('root');
		if (root) {
			root.innerHTML = 'Login complete. You may close this window.';
		}

		microsoftTeams.app.initialize().then(() => {
			//Close the dialog?
			microsoftTeams.authentication.notifySuccess('ConsentComplete');
		});
	} else if (tempRootState.page === 'authStart') {
		inPopup = true;

		if (isTeamsIFrame) {
			microsoftTeams.app.initialize().then(async () => {
				let ctx = await microsoftTeams.app.getContext();

				let spDomain = ctx.sharePointSite?.teamSiteDomain;
				var scopes = apiConfig.defaultGraphScopes.concat([
					'https://' + spDomain + '/AllSites.Manage',
				]);

				requestScopeConsent(scopes);
			});
		} else {
			let webUrl = (tempRootState.argument || '').replace('https://', '');

			//we only need the domain
			let firstSlash = webUrl.indexOf('/');
			if (firstSlash > 0) {
				webUrl = 'https://' + webUrl.substring(0, firstSlash);
			}

			var scopes = apiConfig.defaultGraphScopes.concat([
				webUrl + '/AllSites.Manage',
			]);
			requestScopeConsent(scopes);
		}
	} else if (tempRootState.page === 'authTodo') {
		inPopup = true;

		requestScopeConsent(['https://graph.microsoft.com/Tasks.ReadWrite']);
	} else if (
		(window.location.search || '').startsWith('?et=') ||
		(window.location.search || '').startsWith(
			'?_host_Info=Outlook',
		) /*outlook desktop*/ ||
		(tempRootState.page.startsWith('mail') &&
			!tempRootState.page.endsWith('Harness'))
	) {
		//It's outlook - do the outlook SSO
		let outlookAuth = new OutlookSSOAuth();
		let rootState = new RootState(outlookAuth);

		OutlookCtrlState.waitForOfficeLoad().then(() => {
			
			rootState.useCachedSettings = true;

			Office.onReady((info) => {

				ReactDOM.render(
					<React.StrictMode>
						<PageRouter rootState={rootState} />
					</React.StrictMode>,
					document.getElementById('root'),
				);
			});
		});
		
	} else if (isTeamsIFrame) {
		let teamsAuth = new TeamsSSOAuth();
		let rootState = new RootState(teamsAuth, false);
		rootState.hidePageHeader = true;

		microsoftTeams.app.initialize().then(async () => {
			//var teamsCtx = await microsoftTeams.app.getContext();
			await microsoftTeams.authentication.getAuthToken({});

			microsoftTeams.app.notifySuccess();

			teamsAuth.contextLoad.then((ctx) => {
				//Set the teams argument in the routing. This could be an ID of a task to start editing.
				if (ctx.page.subPageId && !rootState.argument2) {
					//todo
				}

				//This only needs to be done when in a teams context.
				//This is because in a teams context, we're using a Teams token which doesn't given permission to SharePoint.
				//We have to check if that's been granted, and if not, request it.
				rootState.setCurrentUserInfo(ctx.user);

				if (rootState.page === 'configuretab') {
					//Listen for validity changes and pass it to the teams dialog for creating a tab
					//rootState.createProjectState.setValidityStateCallback((valid, config) => {
					//teamsAuth.setValidityState(valid, config);
					//});
				}

				let spUrl =
					ctx.sharePointSite?.teamSiteUrl ||
					ctx.sharePointSite?.teamSiteDomain ||
					'';

				if (spUrl && !spUrl.startsWith('https://')) {
					spUrl = 'https://' + spUrl;
				}

				rootState.spAuth.startCheckSPAuth(spUrl);
			});

			ReactDOM.render(
				<React.StrictMode>
					<AuthenticatedApp RootState={rootState} />
				</React.StrictMode>,
				document.getElementById('root'),
			);
		});
	} else {
		//Embedding within another application - not teams - that implements our SSO calls
		let isSSO = !!RootState.getUrlParameter('sso');
		if (isSSO) {
			var ssoAuth = new SSOAuth();
			let rootState = new RootState(ssoAuth);

			ReactDOM.render(
				<React.StrictMode>
					<PageRouter rootState={rootState} />
				</React.StrictMode>,
				document.getElementById('root'),
			);
		} else {
			//Set to false when in Teams or another embedded/iframe situation
			//Ideally, however, in those situations there's be an ssoToken available and we wouldn't need to log in at all.
			let useRedirectLoginFlow: boolean =
				window.location === window.parent.location;
			var msalAuth = new MSALAuth(useRedirectLoginFlow);
			const msalConfig = MSALConfigGen.getMsalConfig();
			const msalInstance = new PublicClientApplication(msalConfig);

			if (!useRedirectLoginFlow) {
				//If this window is the popup doing a login redirect, then don't load react.
				const contentHash = window.location.hash;
				if (contentHash && (contentHash.startsWith('#code=') || contentHash.startsWith('#id_token='))) {
					let root = document.getElementById('root');
					if (root) {
						root.innerHTML = 'Completing login...';
						inPopup = true;
					}
				}
			}

			if (!inPopup) {
				ReactDOM.render(
					<React.StrictMode>
						<MsalProvider instance={msalInstance}>
							<AppWithMSALAuth auth={msalAuth} />
						</MsalProvider>
					</React.StrictMode>,
					document.getElementById('root'),
				);
			}
		}
	}
}
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
